import React, { useContext } from 'react';
import styled from 'styled-components';
import _ from 'lodash';

import { Section } from 'src/presentation/components/forms/Layout';
import { CustomActionSettings } from './CustomActionSettings';
import FormContext from 'src/presentation/contexts/FormContext';
import { ExternalSourceTypes } from 'src/actions/integrations/commonTypes';
import { DetailedSwitch } from 'src/presentation/components/forms/DetailedSwitch';
import { IntegrationDisplayLabels } from 'src/presentation/components/pages/integrations/constants';
import {
  getActionControls,
  getActionHelpLink,
} from 'src/presentation/components/pages/integrations/actionConfigurations';
import { getActionEditorOptions } from './ActionEditorOptions';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';

export interface ActionSettingsEditorProps {
  integrationType: ExternalSourceTypes;
  dataAid?: string;
}

const ActionSettingsEditor = (props: ActionSettingsEditorProps): JSX.Element => {
  const { dataAid, integrationType } = props;
  const { workingState } = useContext(FormContext);
  const isFeatureEnabled = useIsFeatureEnabled();
  const integrationLabel = IntegrationDisplayLabels[integrationType];

  if (integrationType === ExternalSourceTypes.Ordergroove && !isFeatureEnabled('ordergrooveApiKey')) {
    return <></>;
  }

  const actions = getActionControls(integrationType);
  const hasCustomActionSettings = !_.isEmpty(actions);

  const mainSwitchAttr = 'actionSettings.enabled';

  const { header, mainSwitchHeadingText, mainSwitchEnabledText, mainSwitchDisabledText } = getActionEditorOptions(
    integrationType,
    hasCustomActionSettings,
    integrationLabel
  );
  const mainSwitchHeading = (
    <>
      {mainSwitchHeadingText} are turned {_.get(workingState, mainSwitchAttr) ? 'on' : 'off'}
    </>
  );

  return (
    <Section header="Actions" data-aid={dataAid} headerMargin="small">
      <>
        <StyledP>
          {header}
          <a href={`${getActionHelpLink(integrationType)}`}>Help Docs.</a>
        </StyledP>
        <DetailedSwitch
          attr={mainSwitchAttr}
          conditionalText
          data-aid="action-settings-main-switch"
          disabledText={mainSwitchDisabledText}
          enabledText={mainSwitchEnabledText}
          switchHeading={mainSwitchHeading}
        />
        {_.get(workingState, 'actionSettings.enabled') ? (
          <>
            <CustomActionSettings actions={actions} integrationLabel={integrationLabel} />
          </>
        ) : null}
      </>
    </Section>
  );
};

const StyledP = styled.p`
  margin-top: 0;
  margin-bottom: 16px;
  color: ${p => p.theme.colors.gray700};
`;

export default ActionSettingsEditor;
